/**
 * Keepers › Calculator — what does he cost me next year.
 *
 * Rebuilt to `FFL Pass 04 Calculator.dc.html` and `handoff/calculator-brief.md`. What changed and
 * why, in one paragraph each:
 *
 *   THE PICKER LEADS. The screen used to open on an empty form and ask a manager to type in four
 *   facts the league has already written down. Now it opens on his roster, and choosing a player
 *   fills acquisition, round value, keeper years and drop history from the keeper build. Every
 *   field stays editable afterwards and editing one does NOT drop the player, because "what if
 *   I'd kept him a year longer" is still a question about him.
 *
 *   THE PROSE LEFT. Sixty of the screen's 234 words were rules text — the sleeper-reward
 *   paragraph and the sixty-word argument about why a trade resets nothing. Both are now `§`
 *   marks pointing at the Rulebook. The engine's own one-sentence explanation stays, because it
 *   is about this player rather than about the rules in general.
 *
 *   THE PLATFORM CONTROLS LEFT. Two selects, three number inputs and five checkboxes were
 *   eighteen tap targets under 44px and, on iOS, three different pieces of system furniture over
 *   the answer you were reading. Segments, steppers and toggle rows, all 44px, none of which
 *   opens a keyboard. See `keeper/Controls.tsx`.
 *
 *   THE PHONE IS TWO SCREENS. Inputs, then the answer, with a back chevron. One scroll of
 *   1,979px was the old shape and it meant the verdict was never on screen with the thing that
 *   produced it.
 *
 * NO ARITHMETIC HAPPENS HERE. Every round, every dollar and every season on this screen came out
 * of `keeperCost`, `keeperProjection`, `assessLuxuryTax` or `penaltyRound`, by way of
 * `domain/calculator.ts`. If a figure disagrees with the engine, this file is wrong.
 */

import { useEffect, useMemo, useState } from 'react';
import rows from '../data/keepers-2026.json';
import { LEAGUE_2026 } from '../domain/config.js';
import { faab, openingBalance } from '../domain/faab.js';
import { teamOfGm } from '../domain/identity.js';
import { splitName } from '../domain/keeperRow.js';
import { type KeeperCost, keeperCost, keeperProjection } from '../domain/keepers.js';
import { assessLuxuryTax } from '../domain/luxuryTax.js';
import {
  type CalcInputs, type CalcSource, type YearTile, MANUAL_INPUTS, NFL_ROUND_MAX, NFL_ROUND_MIN,
  ROUND_MAX, ROUND_MIN, YEARS_MAX, YEARS_MIN, displayRound, fourYearCost, gridReadout, inputsFrom,
  pairCostLine,
  recordOf, step, tileLabel, verdictOf, yearTiles,
} from '../domain/calculator.js';
import { CALCULATOR_ANCHORS } from '../domain/rulebookAnchors.js';
import { PlayerClubChip, PositionChip } from './chips.js';
import { Field, Segmented, Stepper, ToggleRow } from './keeper/Controls.js';
import { PlayerPicker } from './keeper/PlayerPicker.js';
import { Linkify, RuleAnchor } from './RuleText.js';
import { DESKTOP, useMedia } from './useMedia.js';
import type { DeclaredKeeper, LeagueStore, Role } from '../store/types.js';
import { dock } from './dock.js';

/** The season the keeper build describes. The screen prices the one after it. */
const SEASON = 2025;
const KEEP_SEASON = SEASON + 1;

const ALL = rows as unknown as CalcSource[];

const ACQUISITIONS = [
  { value: 'drafted', label: 'Drafted' },
  { value: 'udfa', label: 'Waiver' },
  { value: 'farm', label: 'Farm' },
  { value: 'trade', label: 'Trade' },
] as const;

/**
 * The sleeper conditions, short on a phone and full on a desktop.
 *
 * Not an abbreviation of the same sentence — two sentences, both written out, because "Was on NFL
 * IR when drafted" and "Was already on NFL IR when he was drafted" are the same fact and the
 * short one is not a truncation of the long one. Truncating rules copy is how a condition ends up
 * meaning something slightly different at 390px.
 */
const SLEEPER_ROWS = [
  {
    key: 'neverDropped' as const,
    short: 'Never dropped in season',
    long: 'Never dropped during the regular season',
  },
  {
    key: 'onSeasonEndingIR' as const,
    short: 'Enters next season on IR',
    long: 'Enters next season on season-ending IR',
  },
  {
    key: 'onIRBeforeDraft' as const,
    short: 'Was on NFL IR when drafted',
    long: 'Was already on NFL IR when he was drafted',
  },
];

/* ------------------------------------------------------------------------------- the answer */

/** One card's worth of answer: the verdict, the four years, and what the run costs. */
function Answer(
  { inputs, cost, tiles, wide, compact }:
  {
    inputs: CalcInputs;
    cost: KeeperCost;
    tiles: readonly YearTile[];
    wide: boolean;
    /** The compare card, which drops the kicker's year and the remaining-years count. */
    compact?: boolean;
  },
) {
  const verdict = verdictOf(cost, KEEP_SEASON);
  const projection = useMemo(
    () => keeperProjection(recordOf(inputs, SEASON), LEAGUE_2026),
    [inputs],
  );
  const left = projection.length;

  return (
    <>
      <div className={`kcverdict ${verdict.state}`}>
        <span className="kckick">{compact ? String(KEEP_SEASON) : verdict.kicker}</span>
        <span className="kcline">{verdict.line}</span>
        <p className="kcwhy">{verdict.why}</p>
        {!compact && (
          <RuleAnchor anchor={CALCULATOR_ANCHORS.keeperCost} label="Rules" className="kcrules" />
        )}
      </div>

      <div className="kcyears">
        {!compact && (
          <div className="kcyearhead">
            <span className="kcsub">If you keep him every year</span>
            <span className="mut">
              {wide ? `${left} keeper year${left === 1 ? '' : 's'} remaining` : `${left} left`}
            </span>
          </div>
        )}
        <ol className="kctiles">
          {tiles.map((t) => (
            <li key={t.season}
              className={`kctile ${t.kind}${t.current ? ` current ${verdict.state}` : ''}`}>
              <span className="kcseason">{t.season}</span>
              <b>{tileLabel(t, wide && !compact)}</b>
            </li>
          ))}
        </ol>
        {compact && (
          <div className="kcfouryear">
            <span className="mut">Four-year cost</span>
            <b>{fourYearCost(projection)}</b>
          </div>
        )}
      </div>
    </>
  );
}

/* ---------------------------------------------------------------------------------- screen */

export function KeeperCalculator(
  { gm, actor, role, store }:
  { gm: string; actor: string; role: Role; store: LeagueStore },
) {
  const wide = useMedia(DESKTOP);

  /** The player the form is about, or null while it is a hypothetical. */
  const [picked, setPicked] = useState<CalcSource | null>(null);
  const [inputs, setInputs] = useState<CalcInputs>(MANUAL_INPUTS);
  /** Phone only: inputs, then the answer. Ignored from 900px up, where both are on screen. */
  const [showing, setShowing] = useState<'inputs' | 'answer'>('inputs');
  /** The second and third players in the compare view. Desktop only — see the brief's 1c. */
  const [against, setAgainst] = useState<CalcSource[]>([]);

  const [keeperCount, setKeeperCount] = useState(LEAGUE_2026.freeKeepers);
  const [firstYearGM, setFirstYearGM] = useState(false);

  /** What the manager has left to bid with, before the tax lands on it. */
  const [ledger, setLedger] = useState<Array<{ gm?: string; amount: number }>>([]);
  useEffect(() => {
    let live = true;
    void store.faab(KEEP_SEASON).then((f) => {
      if (live) setLedger(f as Array<{ gm?: string; amount: number }>);
    });
    return () => { live = false; };
  }, [store]);

  /*
   * EDITING A FIELD DOES NOT DROP THE PLAYER. A manager who picks Nacua and then winds the years
   * up by one is asking what Nacua would cost after another season, not asking about a stranger
   * with Nacua's round value — so the header keeps his name and his chips. Only `Enter manually`
   * clears him, because that is the control that says "a different question now".
   */
  const set = <K extends keyof CalcInputs>(key: K, value: CalcInputs[K]) =>
    setInputs((prev) => ({ ...prev, [key]: value }));

  const choose = (row: CalcSource) => {
    setPicked(row);
    setInputs(inputsFrom(row));
    if (!wide) setShowing('answer');
  };

  const manual = () => {
    setPicked(null);
    setInputs(MANUAL_INPUTS);
    setAgainst([]);
    if (!wide) setShowing('inputs');
  };

  const record = useMemo(() => recordOf(inputs, SEASON, picked?.player ?? 'preview'), [inputs, picked]);
  const cost = useMemo(() => keeperCost(record, LEAGUE_2026), [record]);
  const projection = useMemo(() => keeperProjection(record, LEAGUE_2026), [record]);
  const tiles = useMemo(() => yearTiles(projection, KEEP_SEASON), [projection]);
  const readout = useMemo(() => gridReadout(inputs, LEAGUE_2026), [inputs]);

  /*
   * THE TAX IS PRICED ON SLOTS, and the engine is what turns a count into a bill. The stepper
   * feeds `assessLuxuryTax` a slate of that many anonymous keepers rather than this file working
   * out which side of the free four the count falls on.
   */
  const tax = useMemo(
    () => assessLuxuryTax(
      {
        teamId: teamOfGm(gm),
        declared: Array.from({ length: keeperCount }, (_, i) => ({ playerId: `slot-${i}` })),
        firstYearGM,
      },
      LEAGUE_2026,
    ),
    [gm, keeperCount, firstYearGM],
  );

  const faabNow = useMemo(() => {
    const start = openingBalance(gm);
    const moved = ledger
      .filter((e) => e.gm?.toUpperCase() === gm.toUpperCase())
      .reduce((n, e) => n + e.amount, 0);
    return start + moved;
  }, [gm, ledger]);
  const faabAfterTax = faabNow - tax.taxFaab;

  /** Everyone in the comparison, the picked player first. */
  const comparing = useMemo(
    () => (picked ? [picked, ...against] : against),
    [picked, against],
  );
  const compared = useMemo(
    () => comparing.map((row) => {
      const inp = row === picked ? inputs : inputsFrom(row);
      const rec = recordOf(inp, SEASON, row.player, row.team);
      return {
        row,
        inputs: inp,
        cost: keeperCost(rec, LEAGUE_2026),
        tiles: yearTiles(keeperProjection(rec, LEAGUE_2026), KEEP_SEASON),
      };
    }),
    [comparing, picked, inputs],
  );

  /** Every rostered player not already in the comparison — what `Add a third` offers. */
  const addable = useMemo(
    () => ALL.filter((r) => !comparing.some((c) => c.player === r.player)),
    [comparing],
  );

  const [addingAgainst, setAddingAgainst] = useState(false);
  const canCompare = wide && picked !== null;
  const inCompare = canCompare && against.length > 0;

  /* ------------------------------------------------------------------- add to the slate */

  const [added, setAdded] = useState<'idle' | 'saving' | 'done'>('idle');
  const mayDeclare = role !== 'viewer' && picked !== null && picked.team === teamOfGm(gm)
    && cost.eligible;

  /**
   * Put him on the slate from here.
   *
   * The calculator is where the decision actually gets made — a manager works out what a player
   * costs and then has to go and remember to tick him somewhere else. This writes the same draft
   * declaration Declare writes, keepers merged rather than replaced, so arriving at Declare shows
   * him already ticked instead of showing an empty slate that has forgotten the last five minutes.
   */
  async function addToSlate() {
    if (!picked) return;
    setAdded('saving');
    const current = await store.declarationFor(KEEP_SEASON, gm);
    const keepers: DeclaredKeeper[] = current?.keepers ?? [];
    if (!keepers.some((k) => k.player === picked.player)) {
      keepers.push({ player: picked.player });
    }
    await store.saveDeclaration({
      season: KEEP_SEASON,
      gm,
      keepers,
      /*
       * ALWAYS A DRAFT. Submitting is a decision with a deadline attached and it belongs on the
       * screen that shows the whole slate; adding one name from the calculator must never be the
       * thing that sends it. A slate already submitted stays submitted — this only ever adds a
       * name to a draft.
       */
      status: current?.status === 'submitted' ? 'submitted' : 'draft',
      updatedBy: actor,
      updatedAt: new Date().toISOString(),
      ...(current?.submittedAt ? { submittedAt: current.submittedAt } : {}),
    });
    setAdded('done');
  }

  useEffect(() => { setAdded('idle'); }, [picked]);

  /* --------------------------------------------------------------------------- the parts */

  const surname = picked ? splitName(picked.player).surname : '';

  const inputsPane = (
    <div className="kcpane">
      <section className="card kccard">
        <h2 className="kcsub">Whose cost?</h2>
        <PlayerPicker roster={ALL} gm={gm} selected={picked} onPick={choose} onManual={manual}
          wide={wide} />
      </section>

      <section className="card kccard">
        <Field label="How was he acquired?">
          <Segmented name="How was he acquired?" value={inputs.acquisition}
            options={ACQUISITIONS} onChange={(v) => set('acquisition', v)} />
        </Field>

        <div className="kcrow">
          <Field label="Round value">
            <Stepper label="Round value" value={inputs.roundValue}
              min={ROUND_MIN} max={ROUND_MAX} down="Lower the round" up="Raise the round"
              onChange={(d) => set('roundValue', step(inputs.roundValue, d, ROUND_MIN, ROUND_MAX))} />
          </Field>
          <Field label="Years kept">
            <Stepper label="Years kept" value={inputs.yearsKept}
              min={YEARS_MIN} max={YEARS_MAX} down="Fewer years kept" up="More years kept"
              onChange={(d) => set('yearsKept', step(inputs.yearsKept, d, YEARS_MIN, YEARS_MAX))} />
          </Field>
          {wide
            ? <Field label="Grid" className="kcgridfield"><span className="kcgrid">{readout.text}</span></Field>
            : null}
        </div>
        {!wide && <span className="kcgrid">{readout.text}</span>}

        {/*
          THE FARM FIELDS APPEAR WITH THE FARM SEGMENT, and the canvas does not draw them.
          A graduate is not priced off the round stepper at all — his first keeper year comes from
          the NFL round that took him (2013-III-1 §4) — so leaving the segment selectable without
          them would let a manager choose Farm and watch nothing change. Built in the same three
          controls as everything else rather than in a new one; flagged for Tim as the one place
          the drawing is silent.
        */}
        {inputs.acquisition === 'farm' && (
          <div className="kcrow kcfarm">
            <Field label="NFL draft round">
              <Stepper label="NFL draft round"
                value={inputs.nflDraftRound === null ? 'Undrafted' : inputs.nflDraftRound}
                min={NFL_ROUND_MIN - 1} max={NFL_ROUND_MAX}
                down="Earlier NFL round" up="Later NFL round"
                onChange={(d) => {
                  const now = inputs.nflDraftRound ?? NFL_ROUND_MIN - 1;
                  const next = step(now, d, NFL_ROUND_MIN - 1, NFL_ROUND_MAX);
                  set('nflDraftRound', next < NFL_ROUND_MIN ? null : next);
                }} />
            </Field>
            <ToggleRow label="Never made a 53-man roster" on={inputs.farmFailedToMakeRoster}
              onChange={(v) => set('farmFailedToMakeRoster', v)} last />
          </div>
        )}

        <div className="kcgroup">
          <div className="kcgrouphead">
            <span className="kclab">Sleeper reward</span>
            <RuleAnchor anchor={CALCULATOR_ANCHORS.sleeperReward} />
          </div>
          {SLEEPER_ROWS.map((r, i) => (
            <ToggleRow key={r.key} label={wide ? r.long : r.short} on={inputs[r.key]}
              onChange={(v) => set(r.key, v)} last={i === SLEEPER_ROWS.length - 1} />
          ))}
        </div>

        {/*
          The trade question has no control, and that is the answer to it: 2013-III-3 resets a
          three-year clock that 2018-III-2 abolished, so a trade changes nothing here. Sixty words
          of the old screen said that; the mark says where to read it.
        */}
        <div className="kcgrouphead kcfoot">
          <span className="mut">A trade changes nothing about keeper standing</span>
          <RuleAnchor anchor={CALCULATOR_ANCHORS.tradeNoReset} />
        </div>
      </section>
    </div>
  );

  const taxCard = (
    <section className="card kccard kctax">
      <div className="kcgrouphead">
        <h2 className="kcsub">Luxury tax</h2>
        <RuleAnchor anchor={CALCULATOR_ANCHORS.luxuryTax} />
      </div>
      <div className="kctaxbody">
        <Field label={wide ? 'Keepers declared' : undefined} className="kctaxcount">
          {!wide && <span className="lab">Keepers declared</span>}
          <Stepper label="Keepers declared" value={keeperCount} min={0} max={LEAGUE_2026.maxKeepers + 1}
            down="One fewer keeper" up="One more keeper"
            onChange={(d) => setKeeperCount((n) => step(n, d, 0, LEAGUE_2026.maxKeepers + 1))} />
        </Field>
        <ToggleRow label="First-year GM amnesty" on={firstYearGM} onChange={setFirstYearGM} last />
        <div className="kcfigs">
          <div className="kcfig">
            <span className="mut">Luxury tax</span>
            <b className={tax.taxFaab > 0 ? 'warn' : ''}>{faab(tax.taxFaab)}</b>
          </div>
          {/*
            Directly under the tax, on every surface that shows tax — the same rule the Declare
            submit sheet follows. It is the number a manager actually asks for: the tax is paid in
            FAAB, so an eighth keeper is FAAB he will not have at the farm auction.
          */}
          <div className="kcfig">
            <span className="mut">FAAB after tax</span>
            <b className={faabAfterTax < 0 ? 'bad' : ''}>{faab(faabAfterTax)}</b>
          </div>
        </div>
      </div>
      <p className="kcnote">
        {tax.freeSlots} free slot{tax.freeSlots === 1 ? '' : 's'}, then{' '}
        {faab(LEAGUE_2026.luxuryTaxByKeeperCount[tax.freeSlots + 1] ?? 0)} per additional keeper.
      </p>
      {tax.errors.map((e) => <p className="kcnote bad" key={e}>{e}</p>)}
    </section>
  );

  const answerPane = (
    <div className="kcpane">
      <section className="card kccard kcanswer">
        <Answer inputs={inputs} cost={cost} tiles={tiles} wide={wide} />
      </section>
      {taxCard}
      {canCompare && !inCompare && (
        <button type="button" className="kcsecondary" onClick={() => setAddingAgainst(true)}>
          Compare with another player
        </button>
      )}
      {!wide && mayDeclare && (
        <button type="button" className="kcprimary" disabled={added !== 'idle'}
          onClick={() => { void addToSlate(); }}>
          {added === 'done' ? `${surname} is on your slate` : `Add ${surname} to my slate`}
        </button>
      )}
    </div>
  );

  /* ------------------------------------------------------------------------ compare (1c) */

  const compareView = (
    <section className="card kccard kccompare">
      <header className="kccomparehead">
        <h2>{compared.map((c) => splitName(c.row.player).surname).join(' or ')}</h2>
        <span className="mut">
          {tax.slotsUsed} of {LEAGUE_2026.maxKeepers} slots used
        </span>
        {compared.length < 3 && (
          <button type="button" className="kcsecondary" onClick={() => setAddingAgainst(true)}>
            Add a third
          </button>
        )}
      </header>

      <div className="kccards">
        {compared.map((c) => (
          <article className="kccardone" key={c.row.player}>
            <header>
              <span className="kcname big">
                <b>{splitName(c.row.player).surname},</b>
                <span>{splitName(c.row.player).given}</span>
              </span>
              <PlayerClubChip player={c.row.player} />
              <PositionChip pos={c.row.pos} />
              <button type="button" className="kcremove"
                aria-label={`Remove ${splitName(c.row.player).surname} from the comparison`}
                onClick={() => {
                  if (c.row === picked) {
                    const [next, ...rest] = against;
                    setPicked(next ?? null);
                    setInputs(next ? inputsFrom(next) : MANUAL_INPUTS);
                    setAgainst(rest);
                  } else {
                    setAgainst((list) => list.filter((r) => r.player !== c.row.player));
                  }
                }}>
                &times;
              </button>
            </header>
            <Answer inputs={c.inputs} cost={c.cost} tiles={c.tiles} wide={wide} compact />
          </article>
        ))}
      </div>

      {/*
        THE BAR IS THE POINT OF THE VIEW. Two verdicts side by side is a lookup; what decides it is
        what the pair costs together, which is a sentence about picks and three figures about FAAB.
      */}
      <div className="kcsummary">
        <div className="kcsumline">
          <span className="mut">Keeping {compared.length === 2 ? 'both' : 'all three'} in {KEEP_SEASON}</span>
          <b>{pairCostLine(compared.map((c) => ({ player: c.row.player, cost: c.cost })))}</b>
        </div>
        <div className="kcfigs">
          <div className="kcfig">
            <span className="mut">Slots used</span>
            <b>{tax.slotsUsed}<i> of {LEAGUE_2026.maxKeepers}</i></b>
          </div>
          <div className="kcfig">
            <span className="mut">Luxury tax</span>
            <b className={tax.taxFaab > 0 ? 'warn' : ''}>{faab(tax.taxFaab)}</b>
          </div>
          <div className="kcfig">
            <span className="mut">FAAB after tax</span>
            <b className={faabAfterTax < 0 ? 'bad' : ''}>{faab(faabAfterTax)}</b>
          </div>
        </div>
      </div>
    </section>
  );

  /* -------------------------------------------------------------------------------- shell */

  return (
    <div className="app kcapp">
      <Linkify>
        {/*
          The phone's answer screen replaces the page header with the player's own — his name and
          his chips, and a way back. That is what stops the screen being one 1,979px scroll.
        */}
        {!wide && showing === 'answer' ? (
          <header className="kcanswerhead">
            <button type="button" className="kcback" aria-label="Back to inputs"
              onClick={() => setShowing('inputs')}>&lsaquo;</button>
            <span className="kcname">
              <b>{surname}{picked ? ',' : ''}</b>
              {picked && <span>{splitName(picked.player).given}</span>}
            </span>
            {picked && <PlayerClubChip player={picked.player} />}
            {picked && <PositionChip pos={picked.pos} />}
          </header>
        ) : (
          <header className="kchead">
            <h1>Keeper Calculator</h1>
            <RuleAnchor anchor={CALCULATOR_ANCHORS.keeperCost} />
            <span className="mut">
              {LEAGUE_2026.draftRounds}-round draft · {LEAGUE_2026.freeKeepers} free keepers ·
              max {LEAGUE_2026.maxKeepers}
            </span>
          </header>
        )}

        {inCompare ? compareView : (
          <div className="kcsplit">
            {(wide || showing === 'inputs') && inputsPane}
            {(wide || showing === 'answer') && answerPane}
          </div>
        )}

        {/* The phone commits from the answer screen; the desktop has both panes on screen. */}
        {!wide && showing === 'inputs' && picked && (
          <button type="button" className="kcprimary" onClick={() => setShowing('answer')}>
            See what {surname} costs
          </button>
        )}
        {wide && mayDeclare && !inCompare && (
          <button type="button" className="kcprimary wide" disabled={added !== 'idle'}
            onClick={() => { void addToSlate(); }}>
            {added === 'done' ? `${surname} is on your slate` : `Add ${surname} to my slate`}
          </button>
        )}

        {/* Docked — see `src/ui/dock.tsx`; `main` is transformed and captures fixed children. */}
        {addingAgainst && dock(
          <>
            <button type="button" className="sheetscrim kcscrim" aria-label="Close"
              onClick={() => setAddingAgainst(false)} />
            <aside className="kcsheet" role="dialog" aria-modal="true"
              aria-label="Add a player to the comparison">
              <header>
                <h2>Compare against</h2>
                <button type="button" className="kcsheetclose" aria-label="Close"
                  onClick={() => setAddingAgainst(false)}>&times;</button>
              </header>
              <ul className="kcroster">
                {addable.filter((r) => r.team === teamOfGm(gm)).map((r) => (
                  <li key={r.player}>
                    <button type="button" onClick={() => {
                      setAgainst((list) => [...list, r]);
                      setAddingAgainst(false);
                    }}>
                      <span className="kcname big">
                        <b>{splitName(r.player).surname},</b>
                        <span>{splitName(r.player).given}</span>
                      </span>
                      <span className="kcrchips">
                        <PlayerClubChip player={r.player} />
                        <PositionChip pos={r.pos} />
                      </span>
                      <span className="kcrval">Rd {displayRound(r, LEAGUE_2026)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          </>
        )}
      </Linkify>
    </div>
  );
}
